import { LegalLayout, LegalSection } from "./LegalLayout";
import { IMAGE_CREDITS } from "../../lib/image-credits";

const linkStyle = { color: "var(--text-accent)" } as const;

/**
 * Aggregated attribution page (photography + data). Photo entries come from
 * lib/image-credits.ts — one entry per CC-licensed image, giving the TASL
 * elements (title, author, source, license) the licenses require.
 */
export default function CreditsPage() {
  return (
    <LegalLayout title="Credits" updated="12 July 2026">
      <LegalSection heading="Photography">
        {IMAGE_CREDITS.length === 0 ? (
          <p>
            Photography credits will be listed here as imagery is added to the site.
          </p>
        ) : (
          <>
            <p>
              The photographs used across Atlas's collections and regions are the work of the
              photographers below, used under Creative Commons licences. Images have been
              resized and cropped to fit unless noted otherwise.
            </p>
            <ul style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 10 }}>
              {IMAGE_CREDITS.map((c) => (
                <li key={c.image}>
                  <a href={c.sourceUrl} target="_blank" rel="noreferrer" style={linkStyle}>
                    {c.title}
                  </a>{" "}
                  by{" "}
                  {c.authorUrl ? (
                    <a href={c.authorUrl} target="_blank" rel="noreferrer" style={linkStyle}>
                      {c.author}
                    </a>
                  ) : (
                    c.author
                  )}
                  ,{" "}
                  <a href={c.licenseUrl} target="_blank" rel="noreferrer" style={linkStyle}>
                    {c.license}
                  </a>
                  {c.modifications ? ` (${c.modifications})` : ""} — {c.usedFor}.
                </li>
              ))}
            </ul>
          </>
        )}
      </LegalSection>

      <LegalSection heading="Data">
        <p>
          Peak data from the{" "}
          <a href="https://www.hills-database.co.uk/" target="_blank" rel="noreferrer" style={linkStyle}>
            Database of British and Irish Hills
          </a>{" "}
          (
          <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer" style={linkStyle}>
            CC BY 4.0
          </a>
          ).
        </p>
        <p>
          Landmark data, car parks and lay-bys, and base-map data ©{" "}
          <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" style={linkStyle}>
            OpenStreetMap contributors
          </a>{" "}
          (ODbL). Map tiles are served by{" "}
          <a href="https://openfreemap.org/" target="_blank" rel="noreferrer" style={linkStyle}>
            OpenFreeMap
          </a>
          .
        </p>
        <p>
          National park boundaries from the Office for National Statistics Open Geography
          Portal and National Trail routes from Natural England open data, both used under
          the{" "}
          <a
            href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
            target="_blank"
            rel="noreferrer"
            style={linkStyle}
          >
            Open Government Licence v3.0
          </a>
          . Contains OS data © Crown copyright and database right 2022.
        </p>
        <p>
          King Charles III England Coast Path route © Natural England copyright, used under
          the Open Government Licence v3.0. Contains Ordnance Survey data © Crown copyright
          and database right 2026.
        </p>
        <p>
          Trig pillar locations and station details from the Ordnance Survey Complete Trig
          Archive, used under the Open Government Licence v3.0. Contains OS data © Crown
          copyright and database right 2026.
        </p>
        <p>
          Place names and postcode areas from OS Open Names, used under the Open Government
          Licence v3.0. Contains OS data © Crown copyright and database rights 2026.
          Contains Royal Mail data © Royal Mail copyright and database right 2026. Contains
          National Statistics data © Crown copyright and database right 2026.
        </p>
        <p>
          Railway station locations from the National Public Transport Access Nodes
          (NaPTAN) dataset, Department for Transport. Contains public sector information
          licensed under the Open Government Licence v3.0.
        </p>
        <p>
          Pembrokeshire Coast Path, Glyndŵr's Way and Wales Coast Path routes from
          Natural Resources Wales open data on DataMapWales, used under the Open Government
          Licence v3.0. Contains Natural Resources Wales information © Natural Resources Wales and Database Right. All rights Reserved. Contains Ordnance Survey Data. Ordnance Survey Licence number AC0000849444. Crown Copyright and Database Right.
        </p>
      </LegalSection>

      <LegalSection heading="Contact">
        <p>
          Spotted a missing or incorrect credit? Let us know:{" "}
          <a href="mailto:hello@goatlas.co.uk" style={linkStyle}>hello@goatlas.co.uk</a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
